import { useEffect, useState } from "react";

import CrudModal from "components/crud/CrudModal";
import CrudInput from "components/crud/CrudInput";
import CrudSelect from "components/crud/CrudSelect";

import {
    createCollection,
    updateCollection,
    uploadCollectionReceipt,
} from "services/api/collectionApi";

import {
    getSchools,
} from "services/api/schoolApi";

import {
    getParties,
} from "services/api/partyApi";

import {
    getVisits,
} from "services/api/visitApi";

import {
    showSuccess,
    showError,
} from "utils/toast";


const INITIAL_FORM = {
    collectionDate: "",
    balanceType: "party",
    schoolId: "",
    partyId: "",
    visitId: "",
    amount: "",
    mode: "cash",
    referenceNo: "",
    chequeNo: "",
    chequeDate: "",
    bankName: "",
    remarks: "",
    receipts: [],
};


export default function CollectionModal({
    open,
    onClose,
    reload,
    item,
}) {

    const isEdit =
        Boolean(item);


    const [loading, setLoading] =
        useState(false);


    const [uploading, setUploading] =
        useState(false);


    const [form, setForm] =
        useState(
            INITIAL_FORM
        );


    const [schools, setSchools] =
        useState([]);


    const [parties, setParties] =
        useState([]);


    const [visits, setVisits] =
        useState([]);



    const loadDropdowns =
        async () => {

            try {

                const [
                    schoolResponse,
                    partyResponse,
                    visitResponse,
                ] = await Promise.all([

                    getSchools(
                        1,
                        100,
                        ""
                    ),

                    getParties(
                        1,
                        100,
                        ""
                    ),

                    getVisits(
                        1,
                        100
                    ),

                ]);


                setSchools(
                    schoolResponse?.data?.schools ||
                    []
                );


                setParties(
                    partyResponse?.data?.parties ||
                    []
                );


                setVisits(
                    visitResponse?.data?.visits ||
                    []
                );

            }
            catch (error) {

                console.error(
                    "Collection dropdown error:",
                    error
                );

            }

        };



    useEffect(() => {

        if (!open) {
            return;
        }


        loadDropdowns();


        if (item) {

            setForm({

                collectionDate:
                    item.collectionDate
                        ? item.collectionDate.substring(
                            0,
                            10
                        )
                        : "",

                balanceType:
                    item.balanceType ||
                    "party",

                schoolId:
                    item.school?.id
                        ? String(
                            item.school.id
                        )
                        : "",

                partyId:
                    item.party?.id
                        ? String(
                            item.party.id
                        )
                        : "",

                visitId:
                    item.visit?.id
                        ? String(
                            item.visit.id
                        )
                        : "",

                amount:
                    item.amount
                        ? String(
                            item.amount
                        )
                        : "",

                mode:
                    item.mode ||
                    "cash",

                referenceNo:
                    item.referenceNo ||
                    "",

                chequeNo:
                    item.chequeNo ||
                    "",

                chequeDate:
                    item.chequeDate
                        ? item.chequeDate.substring(
                            0,
                            10
                        )
                        : "",

                bankName:
                    item.bankName ||
                    "",

                remarks:
                    item.remarks ||
                    "",

                receipts:
                    item.receipts || [],

            });

        }
        else {

            setForm({
                ...INITIAL_FORM,

                collectionDate:
                    new Date()
                        .toISOString()
                        .substring(
                            0,
                            10
                        ),
            });

        }

    }, [
        open,
        item,
    ]);



    const updateField = (
        key,
        value
    ) => {

        setForm(
            (prev) => ({
                ...prev,
                [key]: value,
            })
        );

    };



    const handleReceipt =
        async (event) => {

            const file =
                event.currentTarget.files?.[0];

            if (!file) {
                return;
            }


            const allowedTypes = [
                "image/jpeg",
                "image/png",
                "image/webp",
                "application/pdf",
            ];


            if (
                !allowedTypes.includes(
                    file.type
                )
            ) {

                showError(
                    "Only JPG, PNG, WEBP or PDF files are allowed."
                );

                return;
            }


            if (
                file.size >
                5 * 1024 * 1024
            ) {

                showError(
                    "Receipt must be 5 MB or smaller."
                );

                return;
            }


            try {

                setUploading(
                    true
                );


                const response =
                    await uploadCollectionReceipt(
                        file
                    );


                const filePath =
                    response?.data?.filePath;


                if (!filePath) {

                    throw new Error(
                        "File path not returned."
                    );

                }


                setForm(
                    (prev) => ({

                        ...prev,

                        receipts: [
                            ...prev.receipts,

                            {
                                filePath,
                                fileType:
                                    file.type,
                                caption:
                                    "Payment receipt",
                            },
                        ],

                    })
                );


                showSuccess(
                    "Receipt uploaded successfully."
                );

            }
            catch (error) {

                console.error(
                    error
                );

                showError(
                    error?.response?.data?.message ||
                    "Unable to upload receipt."
                );

            }
            finally {

                setUploading(
                    false
                );

            }

        };



    const removeReceipt = (
        index
    ) => {

        setForm(
            (prev) => ({

                ...prev,

                receipts:
                    prev.receipts.filter(
                        (_, i) =>
                            i !== index
                    ),

            })
        );

    };



    const submit =
        async (e) => {

            e.preventDefault();


            if (
                !form.collectionDate
            ) {

                showError(
                    "Collection date is required."
                );

                return;

            }


            if (
                !form.amount.trim()
            ) {

                showError(
                    "Collection amount is required."
                );

                return;

            }


            if (
                form.balanceType === "school" &&
                !form.schoolId
            ) {

                showError(
                    "Please select a school."
                );

                return;

            }


            if (
                form.balanceType === "party" &&
                !form.partyId
            ) {

                showError(
                    "Please select a party."
                );

                return;

            }


            try {

                setLoading(
                    true
                );


                const payload = {

                    collectionDate:
                        form.collectionDate,

                    balanceType:
                        form.balanceType,

                    schoolId:
                        form.balanceType === "school"
                            ? form.schoolId
                            : undefined,

                    partyId:
                        form.balanceType === "party"
                            ? form.partyId
                            : undefined,

                    visitId:
                        form.visitId ||
                        undefined,

                    amount:
                        form.amount.trim(),

                    mode:
                        form.mode,

                    referenceNo:
                        form.referenceNo.trim() ||
                        undefined,

                    chequeNo:
                        form.chequeNo.trim() ||
                        undefined,

                    chequeDate:
                        form.chequeDate ||
                        undefined,

                    bankName:
                        form.bankName.trim() ||
                        undefined,

                    remarks:
                        form.remarks.trim() ||
                        undefined,

                    receipts:
                        form.receipts.length
                            ? form.receipts
                            : undefined,

                };


                if (isEdit) {

                    await updateCollection(
                        item.id,
                        payload
                    );


                    showSuccess(
                        "Collection updated successfully."
                    );

                }
                else {

                    await createCollection(
                        payload
                    );


                    showSuccess(
                        "Collection created successfully."
                    );

                }


                await reload();

                onClose();

            }
            catch (error) {

                console.error(
                    "Collection save error:",
                    error
                );


                showError(
                    error?.response?.data?.message ||
                    "Unable to save collection."
                );

            }
            finally {

                setLoading(
                    false
                );

            }

        };



    if (!open) {
        return null;
    }



    return (

        <CrudModal

            open={
                open
            }

            onClose={
                onClose
            }

            onSubmit={
                submit
            }

            loading={
                loading ||
                uploading
            }

            title={
                isEdit
                    ? "Edit Collection"
                    : "Create Collection"
            }

            submitText={
                isEdit
                    ? "Update"
                    : "Save"
            }

            width="max-w-5xl"

        >

            <CrudInput
                label="Collection Date"
                type="date"
                value={
                    form.collectionDate
                }
                onChange={(e) =>
                    updateField(
                        "collectionDate",
                        e.target.value
                    )
                }
            />


            <CrudSelect
                label="Balance Type"
                value={
                    form.balanceType
                }
                onChange={(e) => {

                    const value =
                        e.target.value;

                    updateField(
                        "balanceType",
                        value
                    );

                    updateField(
                        "schoolId",
                        ""
                    );

                    updateField(
                        "partyId",
                        ""
                    );

                }}
                options={[
                    {
                        value: "party",
                        label: "Party / Bookseller",
                    },
                    {
                        value: "school",
                        label: "School",
                    },
                ]}
            />


            {
                form.balanceType ===
                    "school" && (

                    <CrudSelect
                        label="School"
                        value={
                            form.schoolId
                        }
                        onChange={(e) =>
                            updateField(
                                "schoolId",
                                e.target.value
                            )
                        }
                        options={[
                            {
                                value: "",
                                label: "Select School",
                            },

                            ...schools.map(
                                (school) => ({
                                    value:
                                        String(
                                            school.id
                                        ),
                                    label:
                                        `${school.schoolCode || ""} - ${school.name}`,
                                })
                            ),
                        ]}
                    />

                )
            }


            {
                form.balanceType ===
                    "party" && (

                    <CrudSelect
                        label="Party / Bookseller"
                        value={
                            form.partyId
                        }
                        onChange={(e) =>
                            updateField(
                                "partyId",
                                e.target.value
                            )
                        }
                        options={[
                            {
                                value: "",
                                label: "Select Party",
                            },

                            ...parties.map(
                                (party) => ({
                                    value:
                                        String(
                                            party.id
                                        ),
                                    label:
                                        `${party.partyCode || ""} - ${party.name}`,
                                })
                            ),
                        ]}
                    />

                )
            }


            <CrudSelect
                label="Visit"
                value={
                    form.visitId
                }
                onChange={(e) =>
                    updateField(
                        "visitId",
                        e.target.value
                    )
                }
                options={[
                    {
                        value: "",
                        label: "Select Visit",
                    },

                    ...visits.map(
                        (visit) => ({

                            value:
                                String(
                                    visit.id
                                ),

                            label:
                                `${
                                    visit.visitDate
                                        ? new Date(
                                            visit.visitDate
                                        ).toLocaleDateString()
                                        : "-"
                                } - ${
                                    visit.school?.name ||
                                    visit.party?.name ||
                                    visit.purpose ||
                                    `Visit #${visit.id}`
                                }`,
                        })
                    ),
                ]}
            />


            <CrudInput
                label="Amount"
                type="number"
                min="0"
                step="0.01"
                value={
                    form.amount
                }
                onChange={(e) =>
                    updateField(
                        "amount",
                        e.target.value
                    )
                }
            />


            <CrudSelect
                label="Payment Mode"
                value={
                    form.mode
                }
                onChange={(e) =>
                    updateField(
                        "mode",
                        e.target.value
                    )
                }
                options={[
                    {
                        value: "cash",
                        label: "Cash",
                    },
                    {
                        value: "upi",
                        label: "UPI",
                    },
                    {
                        value: "bank_transfer",
                        label: "Bank Transfer",
                    },
                    {
                        value: "cheque",
                        label: "Cheque",
                    },
                    {
                        value: "card",
                        label: "Card",
                    },
                    {
                        value: "other",
                        label: "Other",
                    },
                ]}
            />


            {
                (
                    form.mode === "upi" ||
                    form.mode === "bank_transfer" ||
                    form.mode === "card"
                ) && (

                    <CrudInput
                        label="Reference No."
                        value={
                            form.referenceNo
                        }
                        onChange={(e) =>
                            updateField(
                                "referenceNo",
                                e.target.value
                            )
                        }
                    />

                )
            }


            {
                form.mode === "cheque" && (

                    <>
                        <CrudInput
                            label="Cheque No."
                            value={
                                form.chequeNo
                            }
                            onChange={(e) =>
                                updateField(
                                    "chequeNo",
                                    e.target.value
                                )
                            }
                        />

                        <CrudInput
                            label="Cheque Date"
                            type="date"
                            value={
                                form.chequeDate
                            }
                            onChange={(e) =>
                                updateField(
                                    "chequeDate",
                                    e.target.value
                                )
                            }
                        />
                    </>

                )
            }


            {
                form.mode === "bank_transfer" && (

                    <CrudInput
                        label="Bank Name"
                        value={
                            form.bankName
                        }
                        onChange={(e) =>
                            updateField(
                                "bankName",
                                e.target.value
                            )
                        }
                    />

                )
            }


            <div className="mb-4">

                <label
                    className="
                        mb-2
                        block
                        text-sm
                        font-medium
                        text-gray-700
                    "
                >
                    Payment Receipt
                </label>


                <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,.pdf"
                    disabled={
                        uploading ||
                        loading
                    }
                    onChange={
                        handleReceipt
                    }
                    className="
                        block
                        w-full
                        rounded-lg
                        border
                        border-gray-300
                        bg-white
                        p-2
                        text-sm
                    "
                />


                {
                    form.receipts.map(
                        (
                            receipt,
                            index
                        ) => (

                            <div
                                key={
                                    index
                                }
                                className="
                                    mt-2
                                    flex
                                    items-center
                                    justify-between
                                    rounded-lg
                                    bg-green-50
                                    p-3
                                "
                            >

                                <span
                                    className="
                                        break-all
                                        text-xs
                                        text-green-700
                                    "
                                >
                                    ✓ {
                                        receipt.filePath
                                    }
                                </span>


                                <button
                                    type="button"
                                    onClick={() =>
                                        removeReceipt(
                                            index
                                        )
                                    }
                                    className="
                                        ml-3
                                        rounded
                                        bg-red-600
                                        px-2
                                        py-1
                                        text-xs
                                        text-white
                                    "
                                >
                                    Remove
                                </button>

                            </div>

                        )
                    )}

            </div>


            <CrudInput
                label="Remarks"
                value={
                    form.remarks
                }
                onChange={(e) =>
                    updateField(
                        "remarks",
                        e.target.value
                    )
                }
            />

        </CrudModal>
    );
}